import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Spin, Typography, Modal, message, Row, Col, Descriptions, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getDashboard, deleteDashboard,
  getSalesPipelineSummary, getRevenueOverview, getCustomerAnalytics,
  getActivitySummary, getPipelineFunnel,
  type Dashboard, type DashboardWidget,
} from '@/api/dashboardApi';
import { useAuth } from '@/hooks/useAuth';
import { listActivities } from '@/api/activityApi';
import WidgetRenderer from './widgets/WidgetRenderer';

const { Title } = Typography;

// Maps data_source to aggregation API calls
async function fetchWidgetData(widget: DashboardWidget): Promise<unknown> {
  try {
    switch (widget.data_source) {
      case 'sales_pipeline_summary': {
        const res = await getSalesPipelineSummary();
        if (widget.type === 'metric_card') {
          return { value: res.data?.total_open_value ?? 0, prefix: '$' };
        }
        return (res.data?.stage_breakdown ?? []).map((b) => ({
          label: b.key, value: b.doc_count,
        }));
      }
      case 'revenue_overview': {
        const res = await getRevenueOverview();
        if (widget.type === 'metric_card') {
          return { value: res.data?.total_revenue ?? 0, prefix: '$' };
        }
        return (res.data?.monthly_trend ?? []).map((b) => ({
          label: b.date, value: b.doc_count,
        }));
      }
      case 'customer_analytics': {
        const res = await getCustomerAnalytics();
        if (widget.type === 'metric_card') {
          return { value: res.data?.total_customers ?? 0 };
        }
        return (res.data?.industry_breakdown ?? []).map((b) => ({
          label: b.key, value: b.doc_count,
        }));
      }
      case 'activity_summary': {
        const res = await getActivitySummary();
        if (widget.type === 'metric_card') {
          return { value: res.data?.total_activities ?? 0 };
        }
        if (widget.type === 'activity_feed') {
          const activities = await listActivities({ from: 0, size: 10, sort_field: 'scheduled_at', sort_descending: true });
          return (activities.items ?? []).map((a) => ({
            id: a.id,
            subject: a.subject,
            type: a.type,
            status: a.status,
            scheduled_at: a.scheduled_at,
          }));
        }
        return (res.data?.type_breakdown ?? []).map((b) => ({
          label: b.key, value: b.doc_count,
        }));
      }
      case 'pipeline_funnel': {
        const res = await getPipelineFunnel();
        return (res.data?.stages ?? []).map((s) => ({
          label: s.stage_name, value: s.total_value, count: s.opportunity_count,
        }));
      }
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}

export default function DashboardViewPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [widgetData, setWidgetData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [widgetLoading, setWidgetLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('dashboards', 'write');
  const canDelete = hasPermission('dashboards', 'delete');

  const loadDashboard = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getDashboard(id);
      if (res.data) {
        setDashboard(res.data);
        // Fetch data for all widgets in parallel
        setWidgetLoading(true);
        const entries = await Promise.all(
          (res.data.widgets ?? []).map(async (w) => {
            const data = await fetchWidgetData(w);
            return [w.widget_id, data] as const;
          })
        );
        setWidgetData(Object.fromEntries(entries));
        setWidgetLoading(false);
      } else {
        setError(t('errors.dashboard_not_found'));
      }
    } catch {
      setError(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('dashboards.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteDashboard(id);
          message.success(t('dashboards.delete_success'));
          navigate('/dashboards');
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  if (loading) return <Spin size="large" />;
  if (error || !dashboard) {
    return (
      <div>
        <Typography.Text type="danger">{error || t('errors.dashboard_not_found')}</Typography.Text>
        <br />
        <Button onClick={() => navigate('/dashboards')}>{t('app.back')}</Button>
      </div>
    );
  }

  // Sort widgets by position for grid layout
  const sortedWidgets = [...(dashboard.widgets ?? [])].sort((a, b) => {
    if (a.position.y !== b.position.y) return a.position.y - b.position.y;
    return a.position.x - b.position.x;
  });

  // Determine column span from widget size (grid is 24 columns)
  const getColSpan = (w: DashboardWidget) => {
    const width = w.size?.width ?? 6;
    return Math.min(Math.max(width, 4), 24);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboards')}>
            {t('app.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>{dashboard.name}</Title>
        </Space>
        <Space>
          {canWrite && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/dashboards/${id}/edit`)}
            >
              {t('app.edit')}
            </Button>
          )}
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('app.delete')}
            </Button>
          )}
        </Space>
      </div>

      {dashboard.description && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {dashboard.description}
        </Typography.Paragraph>
      )}

      <Descriptions bordered column={3} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('dashboards.field.layout')}>
          <Tag>{dashboard.layout}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboards.field.owner')}>
          {dashboard.owner_id || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboards.field.is_default')}>
          {dashboard.is_default ? <Tag color="green">{t('app.yes')}</Tag> : t('app.no')}
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboards.field.shared_with')} span={2}>
          {(dashboard.shared_with ?? []).length > 0
            ? dashboard.shared_with.map((uid) => <Tag key={uid}>{uid}</Tag>)
            : '—'}
        </Descriptions.Item>
        {dashboard.is_template && (
          <Descriptions.Item label={t('dashboards.field.template_key')}>
            <Tag color="blue">{dashboard.template_key || '—'}</Tag>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Row gutter={[16, 16]}>
        {sortedWidgets.map((widget) => (
          <Col key={widget.widget_id} span={getColSpan(widget)}>
            <WidgetRenderer
              widget={widget}
              data={widgetData[widget.widget_id]}
              loading={widgetLoading}
            />
          </Col>
        ))}
      </Row>

      {sortedWidgets.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: '#8c8c8c' }}>
          {t('dashboards.no_widgets')}
        </div>
      )}
    </div>
  );
}
